import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Phone, Mail, StickyNote, Check, X, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CREW_ROLES } from "@/types/crew";
import { useTranslation } from 'react-i18next';

interface Contact {
  id: string;
  name: string;
  role: string;
  phone?: string;
  email?: string;
  notes?: string;
}

interface ContactsListProps {
  contacts: Contact[];
  onEdit: (id: string, contact: Contact) => void;
  onDelete: (id: string) => void;
}

export default function ContactsList({ contacts, onEdit, onDelete }: ContactsListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Contact | null>(null);
  const { t } = useTranslation();

  // Group contacts by role
  const groupedContacts = contacts.reduce((acc, contact) => {
    const role = contact.role;
    if (!acc[role]) {
      acc[role] = [];
    }
    acc[role].push(contact);
    return acc;
  }, {} as Record<string, Contact[]>);

  const handleEdit = (contact: Contact) => {
    setEditingId(contact.id);
    setEditForm(contact);
  };

  const handleSave = () => {
    if (editForm && editingId) {
      onEdit(editingId, editForm);
      setEditingId(null);
      setEditForm(null);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const handleCopyInfo = (contact: Contact) => {
    const info = [
      `${t('name_label')}: ${contact.name}`,
      `${t('role_label')}: ${t(`crew_role_${contact.role.toLowerCase()}`)}`,
      contact.phone && `${t('phone_label')}: ${contact.phone}`,
      contact.email && `${t('email_label')}: ${contact.email}`,
      contact.notes && `${t('notes_label')}: ${contact.notes}`
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(info).then(() => {
      toast.success(t('contact_info_copied'));
    }).catch(() => {
      toast.error(t('failed_to_copy_contact_info'));
    });
  };

  return (
    <div className="space-y-8">
      {CREW_ROLES.map((role) => {
        const roleContacts = groupedContacts[role] || [];
        if (roleContacts.length === 0) return null;

        return (
          <div key={role} className="space-y-4">
            <h3 className="text-lg font-semibold text-white/80">{t(`crew_role_${role.toLowerCase()}`)}</h3>
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {roleContacts.map((contact) => (
                <Card key={contact.id} className="group bg-black/20 border-0 hover:bg-black/30 transition-all">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    {editingId === contact.id ? (
                      <div className="flex-1 mr-2">
                        <Input
                          value={editForm?.name || ""}
                          onChange={(e) => setEditForm(prev => prev ? { ...prev, name: e.target.value } : null)}
                          placeholder={t('enter_name')}
                          className="bg-black/20 border-0"
                        />
                      </div>
                    ) : (
                      <CardTitle className="text-lg font-semibold line-clamp-1">
                        {contact.name}
                      </CardTitle>
                    )}
                    <div className="flex gap-1">
                      {editingId === contact.id ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            onClick={handleSave}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={handleCancel}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                            onClick={() => handleCopyInfo(contact)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                            onClick={() => handleEdit(contact)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t('delete_contact')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t('delete_contact_confirm', { name: contact.name })} {t('action_cannot_be_undone')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => onDelete(contact.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {t('delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {editingId === contact.id ? (
                        <>
                          <Select
                            value={editForm?.role || ""}
                            onValueChange={(value) => setEditForm(prev => prev ? { ...prev, role: value } : null)}
                          >
                            <SelectTrigger className="bg-black/20 border-0">
                              <SelectValue placeholder={t('select_a_role')} />
                            </SelectTrigger>
                            <SelectContent>
                              {CREW_ROLES.map((role) => (
                                <SelectItem key={role} value={role}>
                                  {t(`crew_role_${role.toLowerCase()}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            value={editForm?.phone || ""}
                            onChange={(e) => setEditForm(prev => prev ? { ...prev, phone: e.target.value } : null)}
                            placeholder={t('enter_phone')}
                            className="bg-black/20 border-0"
                          />
                          <Input
                            value={editForm?.email || ""}
                            onChange={(e) => setEditForm(prev => prev ? { ...prev, email: e.target.value } : null)}
                            placeholder={t('enter_email')}
                            className="bg-black/20 border-0"
                          />
                          <Textarea
                            value={editForm?.notes || ""}
                            onChange={(e) => setEditForm(prev => prev ? { ...prev, notes: e.target.value } : null)}
                            placeholder={t('enter_notes')}
                            className="bg-black/20 border-0 min-h-[80px]"
                          />
                        </>
                      ) : (
                        <>
                          {contact.phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <a href={`tel:${contact.phone}`} className="text-foreground hover:underline">
                                {contact.phone}
                              </a>
                            </div>
                          )}
                          {contact.email && (
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <a href={`mailto:${contact.email}`} className="text-foreground hover:underline break-all">
                                {contact.email}
                              </a>
                            </div>
                          )}
                          {contact.notes && (
                            <div className="flex items-start gap-2 text-sm">
                              <StickyNote className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                              <p className="text-muted-foreground">{contact.notes}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
